import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import type { TileConfig, FontPresetId, DashboardTileLayout, CustomPreset, SavedConfig } from "../types";
import type { WidgetId } from "@/other/constants/EntityManagerConfig";
import { LAYOUT_PRESETS, isDefaultPreset } from "../constants";
import { ENTITY_CONFIGS, type EntityTypeKey } from "@/other/constants/EntityManagerConfig";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useConfirm } from "@omit/react-confirm-dialog";
import { Button } from "@/modern/components/ui/button-shadcn";

function mergeWithWidgetConfig(
  savedTiles: DashboardTileLayout[],
  entityType: EntityTypeKey
): TileConfig[] {
  const config = ENTITY_CONFIGS[entityType];
  if (!config) return [];

  const widgetMap = new Map(config.widgets.map((w) => [w.id, w]));
  const merged: TileConfig[] = [];

  for (const saved of savedTiles) {
    const widget = widgetMap.get(saved.id);
    if (!widget) continue;
    merged.push({
      id: saved.id,
      title: widget.title,
      color: widget.color,
      colSpan: saved.colSpan,
      rowSpan: saved.rowSpan,
      visible: saved.visible,
    });
    widgetMap.delete(saved.id);
  }

  for (const widget of widgetMap.values()) {
    merged.push({
      id: widget.id,
      title: widget.title,
      color: widget.color,
      colSpan: widget.defaultColSpan,
      rowSpan: widget.defaultRowSpan,
      visible: false,
    });
  }

  return merged;
}

function getStorageKey(entityType: EntityTypeKey): string {
  return `${PERSISTENCE_STATE_KEYS.dashboardConfig}-${entityType}`;
}

/** Resolve tiles/columns/fontPreset for a given preset ID. */
function resolvePreset(
  presetId: string,
  customPresets: CustomPreset[]
): { tiles: DashboardTileLayout[]; columns: number; fontPreset: FontPresetId } {
  if (isDefaultPreset(presetId)) {
    const preset = LAYOUT_PRESETS.find((p) => p.id === presetId);
    if (preset) {
      return {
        tiles: preset.tiles,
        columns: preset.columns,
        fontPreset: preset.fontPreset ?? "standard",
      };
    }
  }

  const custom = customPresets.find((p) => p.id === presetId);
  if (custom) {
    return {
      tiles: custom.tiles,
      columns: custom.columns,
      fontPreset: custom.fontPreset ?? "standard",
    };
  }

  const fallback = LAYOUT_PRESETS[0];
  return {
    tiles: fallback.tiles,
    columns: fallback.columns,
    fontPreset: fallback.fontPreset ?? "standard",
  };
}

const DEFAULT_SAVED_CONFIG: SavedConfig = { layoutPreset: "hq-default", customPresets: [] };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadSavedConfig(storageKey: string, userPreferences: any): SavedConfig {
  try {
    const parsed = JSON.parse(userPreferences?.getSetting(storageKey) ?? "null");
    if (!parsed) return DEFAULT_SAVED_CONFIG;
    return {
      layoutPreset: parsed.layoutPreset || "hq-default",
      customPresets: Array.isArray(parsed.customPresets) ? parsed.customPresets : [],
    };
  } catch {
    return DEFAULT_SAVED_CONFIG;
  }
}

export function useDashboardConfigurator(entityType: EntityTypeKey) {
  const { userPreferences } = useSwivuBryntumContext();
  const storageKey = getStorageKey(entityType);

  const initialConfig = useRef(loadSavedConfig(storageKey, userPreferences));

  // Persisted state
  const [savedConfig, setSavedConfig] = useState<SavedConfig>(initialConfig.current);

  // Draft state (what the configurator edits)
  const [customPresets, setCustomPresets] = useState<CustomPreset[]>(initialConfig.current.customPresets);
  const [activeLayoutPreset, setActiveLayoutPreset] = useState<string>(initialConfig.current.layoutPreset);

  const initialResolved = resolvePreset(initialConfig.current.layoutPreset, initialConfig.current.customPresets);
  const [tiles, setTiles] = useState<TileConfig[]>(
    () => mergeWithWidgetConfig(initialResolved.tiles, entityType)
  );
  const [columns, setColumns] = useState<number>(initialResolved.columns);
  const [activeFontPreset, setActiveFontPreset] = useState<FontPresetId>(initialResolved.fontPreset);

  // Saved-state derived values for the dashboard view
  const savedResolved = useMemo(
    () => resolvePreset(savedConfig.layoutPreset, savedConfig.customPresets),
    [savedConfig]
  );
  const savedTiles = useMemo(
    () => mergeWithWidgetConfig(savedResolved.tiles, entityType),
    [savedResolved.tiles, entityType]
  );
  const savedVisibleTiles = useMemo(() => savedTiles.filter((t) => t.visible), [savedTiles]);
  const savedColumns = savedResolved.columns;
  const savedFontPreset = savedResolved.fontPreset;
  const isWarRoom = savedFontPreset === "war-room";

  useEffect(() => {
    if (!isWarRoom) return;

    const style = document.createElement("style");
    style.setAttribute("data-war-room-zoom", "");
    style.textContent = `body > [id*="radix"]:not([data-vaul-drawer]) { zoom: 1.15; }`;
    document.head.appendChild(style);

    return () => style.remove();
  }, [isWarRoom]);

  // Draft-state derived values
  const visibleTiles = useMemo(() => tiles.filter((t) => t.visible), [tiles]);
  const hiddenTiles = useMemo(() => tiles.filter((t) => !t.visible), [tiles]);

  /** Snapshot of what the currently selected preset looks like (for dirty comparison). */
  const activePresetSnapshot = useMemo(() => {
    return resolvePreset(activeLayoutPreset, customPresets);
  }, [activeLayoutPreset, customPresets]);

  /** Whether the draft tiles/columns/font/name differ from the selected preset's stored values. */
  const hasDraftChanges = useMemo(() => {
    const draftTiles = JSON.stringify(
      tiles.map((t) => ({ id: t.id, colSpan: t.colSpan, rowSpan: t.rowSpan, visible: t.visible }))
    );
    const presetTiles = JSON.stringify(
      mergeWithWidgetConfig(activePresetSnapshot.tiles, entityType)
        .map((t) => ({ id: t.id, colSpan: t.colSpan, rowSpan: t.rowSpan, visible: t.visible }))
    );
    const currentPreset = !isDefaultPreset(activeLayoutPreset)
      ? customPresets.find((p) => p.id === activeLayoutPreset)
      : null;
    const savedPreset = !isDefaultPreset(activeLayoutPreset)
      ? savedConfig.customPresets.find((p) => p.id === activeLayoutPreset)
      : null;
    const nameChanged = !!(currentPreset && savedPreset && currentPreset.name !== savedPreset.name);
    return (
      draftTiles !== presetTiles ||
      columns !== activePresetSnapshot.columns ||
      activeFontPreset !== activePresetSnapshot.fontPreset ||
      nameChanged
    );
  }, [tiles, columns, activeFontPreset, activePresetSnapshot, entityType, activeLayoutPreset, customPresets, savedConfig]);

  const isActivePresetApplied = activeLayoutPreset === savedConfig.layoutPreset;
  const isAlreadyApplied = isActivePresetApplied && !hasDraftChanges;
  const showApplyButton = !isAlreadyApplied && !hasDraftChanges;
  const showSaveAndSaveAs = !isDefaultPreset(activeLayoutPreset) && hasDraftChanges;
  const showSaveAsOnly = isDefaultPreset(activeLayoutPreset) && hasDraftChanges;

  // Keep isDirty for close-confirmation (any deviation from saved state, including preset names)
  const isDirty = useMemo(() => {
    const draftSnapshot = JSON.stringify({
      tiles: tiles.map((t) => ({ id: t.id, colSpan: t.colSpan, rowSpan: t.rowSpan, visible: t.visible })),
      columns,
      fontPreset: activeFontPreset,
      layoutPreset: activeLayoutPreset,
      customPresetNames: customPresets.map((p) => ({ id: p.id, name: p.name })),
    });
    const savedSnapshot = JSON.stringify({
      ...resolvePreset(savedConfig.layoutPreset, savedConfig.customPresets),
      layoutPreset: savedConfig.layoutPreset,
      customPresetNames: savedConfig.customPresets.map((p) => ({ id: p.id, name: p.name })),
    });
    return draftSnapshot !== savedSnapshot;
  }, [tiles, columns, activeFontPreset, activeLayoutPreset, savedConfig, customPresets]);

  // --- Tile editing handlers (mark preset as "modified" — no longer auto-set to "custom") ---

  const handleReorder = useCallback((reordered: TileConfig[]) => {
    setTiles((prev) => {
      const hidden = prev.filter((t) => !t.visible);
      return [...reordered, ...hidden];
    });
  }, []);

  const handleResize = useCallback(
    (id: WidgetId, colSpan: number, rowSpan: number) => {
      setTiles((prev) =>
        prev.map((t) => (t.id === id ? { ...t, colSpan, rowSpan } : t))
      );
    },
    []
  );

  const handleToggleVisibility = useCallback((id: WidgetId) => {
    setTiles((prev) =>
      prev.map((t) => (t.id === id ? { ...t, visible: !t.visible } : t))
    );
  }, []);

  const handleAddTile = useCallback((id: WidgetId) => {
    setTiles((prev) =>
      prev.map((t) => (t.id === id ? { ...t, visible: true } : t))
    );
  }, []);

  const handleMoveBetweenContainers = useCallback(
    (id: WidgetId, makeVisible: boolean, overItemId?: WidgetId) => {
      setTiles((prev) => {
        const updated = prev.map((t) =>
          t.id === id ? { ...t, visible: makeVisible } : t
        );

        const visible = updated.filter((t) => t.visible);
        const hidden = updated.filter((t) => !t.visible);

        if (makeVisible && overItemId) {
          const tile = visible.find((t) => t.id === id);
          if (tile) {
            const rest = visible.filter((t) => t.id !== id);
            const overIdx = rest.findIndex((t) => t.id === overItemId);
            if (overIdx !== -1) {
              rest.splice(overIdx, 0, tile);
              return [...rest, ...hidden];
            }
          }
        }

        return [...visible, ...hidden];
      });
    },
    []
  );

  const handleColumnChange = useCallback((count: number) => {
    setColumns(count);
  }, []);

  const handleFontPresetChange = useCallback((presetId: FontPresetId) => {
    setActiveFontPreset(presetId);
  }, []);

  const handleLayoutPresetChange = useCallback((presetId: string) => {
    const resolved = resolvePreset(presetId, customPresets);
    setActiveLayoutPreset(presetId);
    setColumns(resolved.columns);
    setTiles(mergeWithWidgetConfig(resolved.tiles, entityType));
    setActiveFontPreset(resolved.fontPreset);
  }, [customPresets, entityType]);

  // --- Persistence helpers ---

  const persist = useCallback((config: SavedConfig) => {
    userPreferences?.updateSetting(storageKey, JSON.stringify(config));
    setSavedConfig(config);
  }, [userPreferences, storageKey]);

  const [isSaving, setIsSaving] = useState(false);

  const withSavingIndicator = useCallback((fn: () => void) => {
    setIsSaving(true);
    fn();
    setTimeout(() => setIsSaving(false), 800);
  }, []);

  // --- Preset actions ---

  const handleApplyPreset = useCallback(() => {
    withSavingIndicator(() => {
      persist({ layoutPreset: activeLayoutPreset, customPresets });
    });
  }, [activeLayoutPreset, customPresets, persist, withSavingIndicator]);

  const handleSaveAsNewPreset = useCallback((name: string) => {
    withSavingIndicator(() => {
      const newPreset: CustomPreset = {
        id: crypto.randomUUID(),
        name,
        columns,
        fontPreset: activeFontPreset,
        tiles: tiles.map((t) => ({ id: t.id, colSpan: t.colSpan, rowSpan: t.rowSpan, visible: t.visible })),
      };
      const updatedPresets = [...customPresets, newPreset];
      setCustomPresets(updatedPresets);
      setActiveLayoutPreset(newPreset.id);
      persist({ layoutPreset: newPreset.id, customPresets: updatedPresets });
    });
  }, [columns, activeFontPreset, tiles, customPresets, persist, withSavingIndicator]);

  const handleUpdateCustomPreset = useCallback((name?: string) => {
    withSavingIndicator(() => {
      const updatedPresets = customPresets.map((p) =>
        p.id === activeLayoutPreset
          ? {
              ...p,
              ...(name !== undefined ? { name } : {}),
              columns,
              fontPreset: activeFontPreset,
              tiles: tiles.map((t) => ({ id: t.id, colSpan: t.colSpan, rowSpan: t.rowSpan, visible: t.visible })),
            }
          : p
      );
      setCustomPresets(updatedPresets);
      persist({ layoutPreset: activeLayoutPreset, customPresets: updatedPresets });
    });
  }, [activeLayoutPreset, columns, activeFontPreset, tiles, customPresets, persist, withSavingIndicator]);

  const handleDeleteCustomPreset = useCallback((id: string) => {
    const updatedPresets = customPresets.filter((p) => p.id !== id);
    const needsFallback = savedConfig.layoutPreset === id;
    const fallbackPresetId = LAYOUT_PRESETS[0].id;

    setCustomPresets(updatedPresets);

    if (needsFallback || activeLayoutPreset === id) {
      const resolved = resolvePreset(fallbackPresetId, updatedPresets);
      setActiveLayoutPreset(fallbackPresetId);
      setColumns(resolved.columns);
      setTiles(mergeWithWidgetConfig(resolved.tiles, entityType));
      setActiveFontPreset(resolved.fontPreset);
    }

    persist({
      layoutPreset: needsFallback ? fallbackPresetId : savedConfig.layoutPreset,
      customPresets: updatedPresets,
    });
  }, [customPresets, savedConfig, activeLayoutPreset, entityType, persist]);

  const handleRenameCustomPreset = useCallback((id: string, name: string) => {
    setCustomPresets((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
  }, []);

  const handleQuickLayoutPresetChange = useCallback((presetId: string) => {
    const resolved = resolvePreset(presetId, customPresets);
    setActiveLayoutPreset(presetId);
    setColumns(resolved.columns);
    setTiles(mergeWithWidgetConfig(resolved.tiles, entityType));
    setActiveFontPreset(resolved.fontPreset);
    persist({ layoutPreset: presetId, customPresets });
  }, [customPresets, entityType, persist]);

  // --- Discard / close ---

  const handleDiscard = useCallback(() => {
    const resolved = resolvePreset(savedConfig.layoutPreset, savedConfig.customPresets);
    setTiles(mergeWithWidgetConfig(resolved.tiles, entityType));
    setColumns(resolved.columns);
    setActiveFontPreset(resolved.fontPreset);
    setActiveLayoutPreset(savedConfig.layoutPreset);
    setCustomPresets(savedConfig.customPresets);
  }, [savedConfig, entityType]);

  const confirm = useConfirm();

  const handleCloseConfigurator = useCallback(
    async (closeModal: () => void) => {
      if (!isDirty) {
        closeModal();
        return;
      }

      const confirmed = await confirm({
        title: "Unsaved Changes",
        description: "You have unsaved changes. What would you like to do?",
        customActions: ({ confirm: confirmAction, cancel }: { confirm: () => void; cancel: () => void }) => (
          <div className="flex w-full justify-end gap-3">
            <Button variant="ghost" onClick={cancel} size="sm">Keep working</Button>
            <Button variant="destructive" onClick={() => { handleDiscard(); confirmAction(); }} size="sm">Discard</Button>
          </div>
        ),
      });

      if (confirmed) closeModal();
    },
    [isDirty, confirm, handleDiscard]
  );

  return {
    // Draft state (for configurator)
    tiles,
    columns,
    activeFontPreset,
    activeLayoutPreset,
    customPresets,
    visibleTiles,
    hiddenTiles,
    isDirty,
    hasDraftChanges,
    isActivePresetApplied,
    isAlreadyApplied,
    showApplyButton,
    showSaveAndSaveAs,
    showSaveAsOnly,
    handleReorder,
    handleResize,
    handleToggleVisibility,
    handleAddTile,
    handleMoveBetweenContainers,
    handleColumnChange,
    handleFontPresetChange,
    handleLayoutPresetChange,
    handleApplyPreset,
    handleSaveAsNewPreset,
    handleUpdateCustomPreset,
    handleDeleteCustomPreset,
    handleRenameCustomPreset,
    handleDiscard,
    handleCloseConfigurator,
    handleQuickLayoutPresetChange,
    // Saved state (for dashboard view)
    savedVisibleTiles,
    savedColumns,
    savedFontPreset,
    isSaving,
  };
}
